import React, { useMemo, useState } from "react";
import { HiChevronDown, HiChevronUp, HiPlus } from "react-icons/hi";
import { useSwingLibrarySettings } from "../../../swing_library/hooks/useSwingLibrarySettings";
import { IndicatorSelectList } from "../../../swing_library/components/IndicatorSelectList";
import IndicatorsRow from "../../../swing_library/components/IndictorsRow";
import MetricCard from "../MetricCard";
import { useCollectionMetrics } from "../../hooks/useCollectionMetrics";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Tabs from "../../../../components/shared/Tabs/Tabs";
import { buildMetric } from "../../../swing_library/utils/metrics";
import { PiColumnsFill, PiListChecksFill } from "react-icons/pi";
import IndicatorsTableView from "./IndicatorsTableView";

export default function AllIndicators({ collection1, collection2 }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isIndicatorFilterOpen, setIsIndicatorFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState("cards");

  const { data: swingLibrarySettings } = useSwingLibrarySettings();

  const {
    isFetching: isFetchingCollection1,
    isLoading: isLoadingCollection1,
    metrics: metricsCollection1,
    videos: videosCollection1,
  } = useCollectionMetrics(collection1);

  const {
    isFetching: isFetchingCollection2,
    isLoading: isLoadingCollection2,
    metrics: metricsCollection2,
    videos: videosCollection2,
  } = useCollectionMetrics(collection2);

  const indicators = useMemo(
    () => swingLibrarySettings?.savedIndicators ?? [],
    [swingLibrarySettings?.savedIndicators]
  );
  const hasBothCollections = Boolean(collection1 && collection2);

  const viewTabs = useMemo(() => {
    return [
      { key: "cards", title: <PiColumnsFill className="size-5" /> },
      { key: "table", title: <PiListChecksFill className="size-5" /> },
    ];
  }, []);

  const isLoading =
    isLoadingCollection1 ||
    isLoadingCollection2 ||
    isFetchingCollection1 ||
    isFetchingCollection2;

  return (
    <div className="flex flex-col gap-4">
      <IndicatorSelectList
        isOpen={isIndicatorFilterOpen}
        onClose={() => setIsIndicatorFilterOpen(false)}
      />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold text-black">Data</h2>

          <Button
            className="flex items-center gap-1"
            color="gray100"
            onClick={() => setIsCollapsed((prev) => !prev)}
            size="sm"
          >
            {isCollapsed ? "Expand" : "Collapse"}
            {isCollapsed ? (
              <HiChevronDown className="size-5" />
            ) : (
              <HiChevronUp className="size-5" />
            )}
          </Button>
        </div>

        <Tabs
          activeTab={viewMode}
          array={viewTabs}
          className="max-w-[220px]"
          color="gray50/white"
          onTabChange={(tab) => {
            if (tab === "cards" || tab === "table") {
              setViewMode(tab);
            }
          }}
          size="small"
          variant="rounded"
        />
      </div>

      {!isCollapsed && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <IndicatorsRow
              setIsIndicatorFilterOpen={setIsIndicatorFilterOpen}
              showTourAverages={false}
            />
          </div>

          <Divider color="gray200" type="horizontal" />

          {hasBothCollections ? (
            viewMode === "table" ? (
              <IndicatorsTableView
                collection1={collection1}
                collection2={collection2}
                isLoading={isLoading}
                videosCollection1={videosCollection1}
                videosCollection2={videosCollection2}
              />
            ) : (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {indicators.map((indicator) => (
                  <MetricCard
                    collection1={collection1}
                    collection2={collection2}
                    isLoading={isLoading}
                    key={indicator.id}
                    metric1={buildMetric(metricsCollection1, indicator.id)}
                    metric2={buildMetric(metricsCollection2, indicator.id)}
                    title={indicator.name}
                    unit={indicator.unit}
                  />
                ))}
              </div>
            )
          ) : (
            <div className="mt-8 flex flex-col items-center gap-6 text-center">
              <img
                alt="Illustration showing the initial compare state"
                className="ml-6 h-auto w-full md:w-2/3 lg:w-1/2"
                src="/images/collections/initial_compare_card.svg"
              />
              <p className="text-base font-semibold text-black">
                Add data to start comparing
              </p>
              <Button
                color="primary500"
                onClick={() => setIsIndicatorFilterOpen(true)}
                size="sm"
              >
                Add data
                <HiPlus className="size-5 text-white" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
